import { useState, useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { getPaymentsByCompany, formatCurrency } from "../utils/cmsApi";
import { getPaymentTypeLabel } from "../utils/paymentTypes";
import PaymentTooltip from "../components/PaymentTooltip";
import { useSubscription } from "../context/SubscriptionContext";
import { useUser } from "@clerk/react";
import { getWatchedCompany, watchCompany, unwatchCompany } from "../utils/alertsApi";
import CompanyCompareButton from "../components/CompanyCompareButton";

const PRODUCT_TYPE_LABELS = {
  Drug: { label: "Drug", cls: "bg-purple-500/10 border-purple-500/20 text-purple-300" },
  Biological: { label: "Biological", cls: "bg-teal-500/10 border-teal-500/20 text-teal-300" },
  Device: { label: "Device", cls: "bg-blue-500/10 border-blue-500/20 text-blue-300" },
  "Medical Supply": { label: "Medical supply", cls: "bg-green-500/10 border-green-500/20 text-green-300" },
};

function productBadge(type) {
  return PRODUCT_TYPE_LABELS[type] || { label: type || "Product", cls: "bg-white/5 border-white/10 text-white/50" };
}

function getProductLink(name, type) {
  if (type === "Drug" || type === "Biological") {
    return "https://dailymed.nlm.nih.gov/dailymed/search.cfm?query=" + encodeURIComponent(name);
  }
  if (type === "Device" || type === "Medical Supply") {
    return "https://accessgudid.nlm.nih.gov/devices/search?query=" + encodeURIComponent(name);
  }
  return null;
}

function getProductLinkLabel(type) {
  if (type === "Drug" || type === "Biological") return "DailyMed →";
  if (type === "Device" || type === "Medical Supply") return "AccessGUDID →";
  return null;
}

function ProductCard({ p }) {
  const badge = productBadge(p.type);
  const link = getProductLink(p.name, p.type);
  const linkLabel = getProductLinkLabel(p.type);
  const googleUrl = "https://www.google.com/search?q=" + encodeURIComponent(p.name + " " + (p.type || "medical product"));

  return (
    <div className="card py-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-white/80 text-sm">{p.name}</h3>
          <div className="flex gap-3 mt-1.5">
            {link && (
              <a href={link} target="_blank" rel="noopener noreferrer" className="text-gold-400/70 hover:text-gold-400 text-xs transition-colors">
                {linkLabel}
              </a>
            )}
            <a href={googleUrl} target="_blank" rel="noopener noreferrer" className="text-white/20 hover:text-white/50 text-xs transition-colors">
              Search →
            </a>
          </div>
        </div>
        <span className={"badge border shrink-0 " + badge.cls}>{badge.label}</span>
      </div>
    </div>
  );
}

export default function CompanyPage({ year }) {
  const { npi, companyName } = useParams();
  const [searchParams] = useSearchParams();
  const physicianName = searchParams.get("name") || "This physician";
  const decodedCompany = decodeURIComponent(companyName);
  const { isPro, setShowUpgradeModal, setUpgradeMessage } = useSubscription();
  const { user, isSignedIn } = useUser();  
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllRecords, setShowAllRecords] = useState(false);
  const [isWatched, setIsWatched] = useState(false);
  const [watchLoading, setWatchLoading] = useState(false);  

  useEffect(() => {
    setLoading(true);
    setCompanyData(null);
    getPaymentsByCompany(npi, year)
      .then((companies) => {
        const found = companies.find((c) => c.companyName === decodedCompany);
        setCompanyData(found || null);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [npi, decodedCompany, year]);

// Check if company is already watched
  useEffect(() => {
    if (!isSignedIn || !user) return;
    getWatchedCompany(user.id, decodedCompany)
      .then((data) => setIsWatched(!!data))
      .catch(() => {});
  }, [isSignedIn, user, decodedCompany]);
  
async function handleWatchToggle() {
    if (!isSignedIn) {
      setUpgradeMessage("Sign in to watch companies and receive email alerts.");
      setShowUpgradeModal(true);
      return;
    }
    if (!isPro) {
      setUpgradeMessage("Watching companies and receiving email alerts is a Pro feature. Upgrade to get notified when new payment data is published.");
      setShowUpgradeModal(true);
      return;
    }
    setWatchLoading(true);
    try {
      if (isWatched) {
        await unwatchCompany(user.id, decodedCompany);
        setIsWatched(false);
      } else {
        await watchCompany(user.id, decodedCompany);
        setIsWatched(true);
      }
    } catch (err) {
      console.error("Watch toggle error:", err);
    } finally {
      setWatchLoading(false);
    }
  }

  const products = companyData
    ? Array.from(
        new Map(
          companyData.payments
            .flatMap((p) => [
              p.product1 && p.product1.trim() ? { name: p.product1, type: p.productType1 } : null,
              p.product2 && p.product2.trim() ? { name: p.product2, type: p.productType2 } : null,
              p.product3 && p.product3.trim() ? { name: p.product3, type: p.productType3 } : null,
              p.product4 && p.product4.trim() ? { name: p.product4, type: p.productType4 } : null,
              p.product5 && p.product5.trim() ? { name: p.product5, type: p.productType5 } : null,
            ])
            .filter(Boolean)
            .map((p) => [p.name, p])
        ).values()
      )
    : [];

  const visibleRecords = isPro || showAllRecords
    ? companyData?.payments || []
    : (companyData?.payments || []).slice(0, 10);

  return (
    <main className="max-w-3xl mx-auto px-4 py-10">
      <Link to={-1} className="text-white/40 hover:text-white/70 text-sm transition-colors">
        back to {physicianName}
      </Link>

      <div className="mt-4 mb-8">
      <div className="flex items-center justify-between gap-4 mt-2">
          <h1 className="font-display text-3xl text-white leading-tight">{decodedCompany}</h1>
          <button
            onClick={handleWatchToggle}
            disabled={watchLoading}
            className={`flex items-center gap-1.5 text-xs px-3 py-1 rounded-lg border transition-all shrink-0 ${
              isWatched
                ? "text-gold-400 border-gold-500/30 bg-gold-500/10"
                : "text-white/60 border-white/20 hover:text-white hover:border-white/40 bg-white/5"
            }`}
          >
            <span>{isWatched ? "🔔" : "🔕"}</span>
            <span>{watchLoading ? "..." : isWatched ? "Watching" : "Watch"}</span>
          </button>
        </div>
        {companyData && (
          <p className="text-white/40 text-sm mt-1">
            {formatCurrency(companyData.total)} total · {companyData.count} payment{companyData.count !== 1 ? "s" : ""} to {physicianName}
          </p>
        )}        
      </div>

      {loading && (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-4 bg-white/10 rounded w-1/2 mb-2" />
              <div className="h-3 bg-white/5 rounded w-1/3" />
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="card border-red-500/20 bg-red-500/5 text-red-300 text-sm">
          {error}
        </div>
      )}

      {!loading && !error && companyData && (
        <div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
            <section>
              <h2 className="text-white/40 text-xs uppercase tracking-widest mb-3">Payment breakdown</h2>
              <div className="card divide-y divide-white/5">
                {Object.entries(companyData.categories)
                  .sort((a, b) => b[1] - a[1])
                  .map(([cat, amount]) => {
                    const catCount = companyData.payments.filter(
                      (p) => p.natureOfPayment === cat
                    ).length;
                    const avg = catCount > 0 ? amount / catCount : 0;
                    return (
                      <div key={cat} className="py-3">
                        <div className="flex justify-between items-center">
                          <div className="flex items-center">
                            <span className="text-white/70 text-sm">{getPaymentTypeLabel(cat)}</span>                            
                            <PaymentTooltip category={cat} />
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-white/30 text-xs font-mono">
                              {((amount / companyData.total) * 100).toFixed(1)}%
                            </span>
                            <span className="text-gold-400 font-mono text-sm">{formatCurrency(amount)}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-white/25 text-xs">
                            {catCount} payment{catCount !== 1 ? "s" : ""} · avg {formatCurrency(avg)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                <div className="flex justify-between items-center py-3">
                  <span className="text-white font-medium text-sm">Total</span>
                  <span className="text-gold-400 font-mono font-medium">{formatCurrency(companyData.total)}</span>
                </div>
              </div>              
            </section>

            <section>
              <h2 className="text-white/40 text-xs uppercase tracking-widest mb-3">
                Associated products &amp; services
              </h2>
              {products.length > 0 ? (
                <div className="space-y-2">
                  {products.map((p) => (
                    <ProductCard key={p.name} p={p} />
                  ))}
                </div>
              ) : (
                <div className="card text-center py-6">
                  <p className="text-white/30 text-sm">No products listed for these payments.</p>
                </div>
              )}
            </section>
          </div>

          {/* Payment records */}
          <section>
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-white/40 text-xs uppercase tracking-widest">
                Payment records
              </h2>
              <div className="flex items-center gap-3">
                <span className="text-white/30 text-xs">
                  {isPro || showAllRecords
                    ? `All ${companyData.payments.length} records`
                    : `Latest 10 of ${companyData.payments.length}`}
                </span>
                {isPro ? (
                  <button
                    onClick={() => exportPaymentsCsv(companyData.payments, decodedCompany, physicianName)}
                    className="text-gold-400/60 hover:text-gold-400 text-xs transition-colors"
                  >
                    ⬇ Export CSV
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setUpgradeMessage("Exporting payment records is a Pro feature. Upgrade to download the full payment history as a CSV.");
                      setShowUpgradeModal(true);
                    }}
                    className="text-white/20 hover:text-white/40 text-xs transition-colors"
                  >
                    🔒 Export CSV
                  </button>
                )}                
              </div>
            </div>            

            <div className="space-y-2">
              {visibleRecords.map((p, i) => (
                <div key={p.id || i} className="card py-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="text-white/70 text-sm">{getPaymentTypeLabel(p.natureOfPayment)}</div>                      
                      {p.product1 && p.product1.trim() && (
                        <div className="text-white/30 text-xs mt-0.5">
                          {[p.product1, p.product2, p.product3, p.product4, p.product5]
                            .filter((x) => x && x.trim())
                            .join(", ")}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-gold-400 font-mono text-sm">{formatCurrency(p.amount)}</div>
                      {p.date && (
                        <div className="text-white/25 text-xs">
                          {new Date(p.date).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Paywall or show all button */}
            {!isPro && !showAllRecords && companyData.payments.length > 10 && (
              <div className="card border-gold-500/20 bg-gold-500/5 text-center py-6 mt-3">
                <p className="text-gold-400 font-medium text-sm mb-1">
                  🔒 {companyData.payments.length - 10} more payment records hidden
                </p>
                <p className="text-white/40 text-xs mb-3">
                  Upgrade to Pro to see all payment records
                </p>
                <button
                  onClick={() => {
                    setUpgradeMessage(`You're seeing 10 of ${companyData.payments.length} payment records. Upgrade to Pro to see all of them.`);
                    setShowUpgradeModal(true);
                  }}
                  className="btn-primary text-xs px-4 py-2"
                >
                  Upgrade to Pro
                </button>                
              </div>
            )}
            {isPro && !showAllRecords && companyData.payments.length > 10 && (
              <button
                onClick={() => setShowAllRecords(true)}
                className="w-full text-center text-white/40 hover:text-white/70 text-xs py-3 transition-colors"
              >
                Show all {companyData.payments.length} records
              </button>
            )}
          </section>

          <div className="mt-10 space-y-3">
            <p className="text-white/30 text-xs text-center leading-relaxed">
              Products shown are only those explicitly linked to payments made to this physician by this company. Payments for general consulting, meals, or advisory services may not list a specific product, so this list may not be complete.
            </p>
            <p className="text-white/20 text-xs text-center">
              Source: CMS Open Payments · Data reported by manufacturers under the Sunshine Act
            </p>
          </div>
        </div>
      )}

      {!loading && !companyData && !error && (
        <div className="card text-center py-10">
          <p className="text-white/40 text-sm">No data found for this company.</p>
        </div>
      )}
    </main>
  );
  
}

function exportPaymentsCsv(payments, companyName, physicianName) {
  const rows = [["Date", "Nature of Payment", "Amount", "Products"]];
  for (const p of payments) {
    rows.push([
      p.date ? new Date(p.date).toLocaleDateString() : "",
      p.natureOfPayment || "",
      p.amount ? p.amount.toFixed(2) : "0.00",
      [p.product1, p.product2, p.product3, p.product4, p.product5]
        .filter((x) => x && x.trim())
        .join("; "),
    ]);
  }
  const csv = rows.map((r) => r.map((v) => `"${v}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${physicianName.replace(/\s+/g, "_")}_${companyName.replace(/\s+/g, "_")}_payments.csv`;
  a.click();
}